import { css } from 'styled-components';
import {
  FieldType,
  EmailDataType,
  InAppDataType,
  InboxDataType,
  PushDataType,
  SmsDataType,
  WebDataType,
  GenericDataType,
  DataTypeType,
  JourneyDataType,
  WhatsAppDataType,
} from 'modules/domains';
import { Product, Team } from 'teams';
import { ConditionalHandlebarMacro } from 'modules/handlebars/domain/macro/types';
import {
  NumericComparator,
  StringComparator,
  BooleanComparator,
  DynamicFieldType,
  DateComparator,
  ComparatorType,
  Condition,
  ConditionalContentClause,
  Combinator,
} from './domain';
import { StringValue } from './domain/condition/StringValue';

/** create mappings for comparators based on handle bars type */
export const fieldTypeToComparators: Record<DynamicFieldType, ComparatorType[]> = {
  [FieldType.STRING]: [
    StringComparator.Equals,
    StringComparator.GreaterThan,
    StringComparator.GreaterThanOrEqual,
    StringComparator.LessThan,
    StringComparator.LessThanOrEqual,
    StringComparator.MatchesRegex,
    StringComparator.Contains,
  ],
  [FieldType.INTEGER]: [
    NumericComparator.Equals,
    NumericComparator.GreaterThan,
    NumericComparator.GreaterThanOrEqual,
    NumericComparator.LessThan,
    NumericComparator.LessThanOrEqual,
  ],
  [FieldType.FLOAT]: [
    NumericComparator.Equals,
    NumericComparator.GreaterThan,
    NumericComparator.GreaterThanOrEqual,
    NumericComparator.LessThan,
    NumericComparator.LessThanOrEqual,
  ],
  [FieldType.DATE]: [
    DateComparator.Exactly,
    DateComparator.After,
    DateComparator.OnOrAfter,
    DateComparator.Before,
    DateComparator.OnOrBefore,
  ],
  [FieldType.BOOLEAN]: [BooleanComparator.Equals, BooleanComparator.DoesNotEqual],
};

/** creates mappings for handlebars macros based on comparator */
export const comparatorToConditionalHandlebarMacro: Record<
  ComparatorType,
  ConditionalHandlebarMacro
> = {
  [StringComparator.MatchesRegex]: ConditionalHandlebarMacro.IfMatchesRegexStr,
  [StringComparator.Contains]: ConditionalHandlebarMacro.ContainsStr,
  [StringComparator.Equals]: ConditionalHandlebarMacro.Eq,
  [StringComparator.GreaterThan]: ConditionalHandlebarMacro.Gt,
  [StringComparator.GreaterThanOrEqual]: ConditionalHandlebarMacro.Gte,
  [StringComparator.LessThan]: ConditionalHandlebarMacro.Lt,
  [StringComparator.LessThanOrEqual]: ConditionalHandlebarMacro.Lte,
  [NumericComparator.Equals]: ConditionalHandlebarMacro.Eq,
  [NumericComparator.GreaterThan]: ConditionalHandlebarMacro.Gt,
  [NumericComparator.GreaterThanOrEqual]: ConditionalHandlebarMacro.Gte,
  [NumericComparator.LessThan]: ConditionalHandlebarMacro.Lt,
  [NumericComparator.LessThanOrEqual]: ConditionalHandlebarMacro.Lte,
  [BooleanComparator.Equals]: ConditionalHandlebarMacro.Eq,
  [BooleanComparator.DoesNotEqual]: ConditionalHandlebarMacro.Not,
  [DateComparator.Exactly]: ConditionalHandlebarMacro.Eq,
  [DateComparator.After]: ConditionalHandlebarMacro.Gt,
  [DateComparator.OnOrAfter]: ConditionalHandlebarMacro.Gte,
  [DateComparator.Before]: ConditionalHandlebarMacro.Lt,
  [DateComparator.OnOrBefore]: ConditionalHandlebarMacro.Lte,
};

export const comparatorToSymbol: Record<ComparatorType, string | undefined> = {
  [StringComparator.MatchesRegex]: undefined,
  [StringComparator.Contains]: undefined,
  [StringComparator.Equals]: '=',
  [StringComparator.GreaterThan]: '>',
  [StringComparator.GreaterThanOrEqual]: '≥',
  [StringComparator.LessThan]: '<',
  [StringComparator.LessThanOrEqual]: '≤',
  [NumericComparator.Equals]: '=',
  [NumericComparator.GreaterThan]: '>',
  [NumericComparator.GreaterThanOrEqual]: '≥',
  [NumericComparator.LessThan]: '<',
  [NumericComparator.LessThanOrEqual]: '≤',
  [BooleanComparator.Equals]: '=',
  [BooleanComparator.DoesNotEqual]: '≠',
  [DateComparator.Exactly]: '=',
  [DateComparator.After]: '>',
  [DateComparator.OnOrAfter]: '≥',
  [DateComparator.Before]: '<',
  [DateComparator.OnOrBefore]: '≤',
};

export const comparatorToLabelTranslationKey: Record<ComparatorType, string> = {
  [StringComparator.MatchesRegex]: 'conditionalContent.comparators.string.matchesRegex',
  [StringComparator.Contains]: 'conditionalContent.comparators.string.contains',
  [StringComparator.Equals]: 'conditionalContent.comparators.string.equals',
  [StringComparator.GreaterThan]: 'conditionalContent.comparators.string.greaterThan',
  [StringComparator.GreaterThanOrEqual]: 'conditionalContent.comparators.string.greaterThanOrEqual',
  [StringComparator.LessThan]: 'conditionalContent.comparators.string.lessThan',
  [StringComparator.LessThanOrEqual]: 'conditionalContent.comparators.string.lessThanOrEqual',
  [NumericComparator.Equals]: 'conditionalContent.comparators.numeric.equals',
  [NumericComparator.GreaterThan]: 'conditionalContent.comparators.numeric.greaterThan',
  [NumericComparator.GreaterThanOrEqual]:
    'conditionalContent.comparators.numeric.greaterThanOrEqual',
  [NumericComparator.LessThan]: 'conditionalContent.comparators.numeric.lessThan',
  [NumericComparator.LessThanOrEqual]: 'conditionalContent.comparators.numeric.lessThanOrEqual',
  [BooleanComparator.Equals]: 'conditionalContent.comparators.boolean.equals',
  [BooleanComparator.DoesNotEqual]: 'conditionalContent.comparators.boolean.doesNotEqual',
  [DateComparator.Exactly]: 'conditionalContent.comparators.date.exactly',
  [DateComparator.After]: 'conditionalContent.comparators.date.after',
  [DateComparator.OnOrAfter]: 'conditionalContent.comparators.date.onOrAfter',
  [DateComparator.Before]: 'conditionalContent.comparators.date.before',
  [DateComparator.OnOrBefore]: 'conditionalContent.comparators.date.onOrBefore',
};

export const I18N_NAMESPACE = 'dynamicContentBuilder';

export const BLANK_CONDITION: Condition = {
  fieldName: '',
  value: new StringValue(''),
  comparator: NumericComparator.Equals,
  id: '',
};

export const BLANK_CONDITIONAL_CONTENT_CLAUSE: ConditionalContentClause = {
  conditions: [],
  combinator: Combinator.OR,
  id: '',
};

const emailDataTypes = Object.values(EmailDataType);
const inAppDataTypes = Object.values(InAppDataType);
const inboxDataTypes = Object.values(InboxDataType);
const pushDataTypes = Object.values(PushDataType);
const smsDataTypes = Object.values(SmsDataType);
const webDataTypes = Object.values(WebDataType);

export const systemEvents: DataTypeType[] = [
  ...emailDataTypes,
  ...inAppDataTypes,
  ...inboxDataTypes,
  ...pushDataTypes,
  ...smsDataTypes,
  ...webDataTypes,
  GenericDataType.Purchase,
  GenericDataType.Subscribe,
  GenericDataType.UnSubscribe,
];

export const customEvent: DataTypeType = GenericDataType.CustomEvent;
export const allEvents = [customEvent, ...systemEvents];

export const eventLabels: Record<DataTypeType, string> = {
  [GenericDataType.Purchase]: 'Purchase',
  [GenericDataType.Subscribe]: 'Subscribe',
  [GenericDataType.UnSubscribe]: 'Unsubscribe',
  [GenericDataType.CustomEvent]: 'Custom Event',
  [GenericDataType.User]: 'User',
  [JourneyDataType.JourneyExit]: 'Journey Exit',
  [EmailDataType.Bounce]: 'Email Bounce',
  [EmailDataType.Click]: 'Email Click',
  [EmailDataType.Complaint]: 'Email Complaint',
  [EmailDataType.HostedUnsubscribeClick]: 'Hosted Unsubscribe Click',
  [EmailDataType.Open]: 'Email Open',
  [EmailDataType.Send]: 'Email Send',
  [EmailDataType.SendSkip]: 'Email Send Skip',
  [InAppDataType.Click]: 'In-App Click',
  [InAppDataType.Close]: 'In-App Close',
  [InAppDataType.Delete]: 'In-App Delete',
  [InAppDataType.Delivery]: 'In-App Delivery',
  [InAppDataType.Open]: 'In-App Open',
  [InAppDataType.Send]: 'In-App Send',
  [InAppDataType.SendSkip]: 'In-App Send Skip',
  [InboxDataType.MessageImpression]: 'Inbox Message Impression',
  [InboxDataType.Session]: 'Inbox Session',
  [PushDataType.Bounce]: 'Push Bounce',
  [PushDataType.Open]: 'Push Open',
  [PushDataType.Send]: 'Push Send',
  [PushDataType.SendSkip]: 'Push Send Skip',
  [PushDataType.Uninstall]: 'Push Uninstall',
  [SmsDataType.Bounce]: 'SMS Bounce',
  [SmsDataType.Click]: 'SMS Click',
  [SmsDataType.Received]: 'SMS Received',
  [SmsDataType.Send]: 'SMS Send',
  [SmsDataType.SendSkip]: 'SMS Send Skip',
  [SmsDataType.UsageInfo]: 'SMS Usage Info',
  [WebDataType.PushClick]: 'Web Push Click',
  [WebDataType.PushSend]: 'Web Push Send',
  [WebDataType.PushSendSkip]: 'Web Push Send Skip',
  [WhatsAppDataType.WhatsAppBounce]: 'WhatsApp Bounce',
  [WhatsAppDataType.WhatsAppClick]: 'WhatsApp Click',
  [WhatsAppDataType.WhatsAppReceived]: 'WhatsApp Received', // WhatsApp Reply
  [WhatsAppDataType.WhatsAppSeen]: 'WhatsApp Seen',
  [WhatsAppDataType.WhatsAppSend]: 'WhatsApp Send',
  [WhatsAppDataType.WhatsAppSendSkip]: 'WhatsApp Send Skip',
};

export const CONDITIONS_LIMIT = 5;
export const CLAUSES_LIMIT = 5;

export const DOCS_URL = 'https://support.iterable.com/hc/articles/19979187092756';

export const DYNAMIC_CONTENT_RUM_ATTRIBUTES = {
  teamName: Team.AUDIENCE_TEAM,
  productName: Product.DYNAMIC_CONTENT_BUILDER,
};

export const SNIPPET_NAME_QUERY_BATCH_SIZE = 100;
export const HANDLEBAR_REGEX = /\{{2}.*\}{2}/i;

export const DYNAMIC_CONTENT_INPUT_VARIANTS = {
  DEFAULT: css`
    background: ${({ theme }) => theme.colors.primaryColors.purple10};
    color: ${({ theme }) => theme.colors.textColors.textLink};
    border: ${({ theme }) =>
      `solid ${theme.spacing.values.eighth} ${theme.colors.primaryColors.purple10}`};
  `,
  ERROR: css`
    border: ${({ theme }) =>
      `solid ${theme.spacing.values.single} ${theme.colors.primaryColors.red100}`};
    background: #fff;
    color: ${({ theme }) => theme.colors.primaryColors.red100};
  `,
  DISABLED: css`
    background: ${({ theme }) => theme.colors.backgroundColors.backgroundSecondary};
    border: ${({ theme }) => `solid ${theme.spacing.values.single} transparent`};
    color: ${({ theme }) => theme.colors.textColors.textDisabled};
  `,
  OPEN: css`
    background: #fff;
    border: ${({ theme }) =>
      `solid ${theme.spacing.values.single} ${theme.colors.primaryColors.purple50}`};
    color: ${({ theme }) => theme.colors.textColors.textLink};
  `,
};

export enum DCB_MODES {
  ASSISTED = 'assisted',
  MANUAL = 'manual',
}
